import React, { useState, useEffect } from 'react';
import api from './api';
import './styles/index.css';

const AdminHome = ({ 
  onGoToLigas, 
  onGoToTorneos, 
  onCrearLiga, 
  onCrearTorneo,
  onGoToGestionUsuarios,
  onGoToTraining,
  onGoToControlPartidas,
  onLogout 
}) => {
  const [user] = useState(api.getCurrentUser());
  const esRecaudador = user?.tipoUsuario === 'recaudador';
  const [stats, setStats] = useState({
    ligasActivas: 0,
    torneosActivos: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const [ligas, campeonatos] = await Promise.all([
        api.getMyLeagues(),
        api.getAllCampeonatos()
      ]);
      
      // Contar ligas activas
      const ligasActivas = ligas.filter(l => l.estado === 'activa').length;
      
      // Contar torneos activos (del usuario)
      const userId = user?._id || user?.id;
      const misTorneos = campeonatos.filter(c => {
        const adminId = c.administrador?._id || c.administrador;
        return adminId === userId;
      });
      const torneosActivos = misTorneos.filter(c => c.estado === 'activo').length;
      
      setStats({
        ligasActivas,
        torneosActivos,
        totalLigas: ligas.length,
        totalTorneos: misTorneos.length
      });
    } catch (err) {
      console.error('Error cargando estadísticas:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    api.logout();
    if (onLogout) onLogout();
    window.location.reload();
  };

  return (
    <div className="dashboard-container">
      {/* Header Profesional */}
      <div className="dashboard-header dashboard-header-pro">
        {/* Izquierda: Logo + Nombre */}
        <div className="header-brand">
          <img src="/Logo26.png" alt="501 Dart League" className="dashboard-logo" />
          <div className="header-brand-text">
            <h1>501 Dart League</h1>
            <span className="header-brand-subtitle">Gestión de Competiciones</span>
          </div>
        </div>
        
        {/* Derecha: Usuario + Acciones */}
        <div className="header-user-section">
          <div className="header-user-info">
            <span className="header-greeting">Hola, <strong>{user?.nombre || user?.username}</strong></span>
            <span className="admin-badge">⚙️ Administrador</span>
          </div>
          <button onClick={handleLogout} className="logout-button">
            Cerrar sesión
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className="admin-home-main">

        {/* Stats Cards */}
        <section className="admin-home-stats">
          <div className="admin-stat-card">
            <div className="admin-stat-icon ligas">🏆</div>
            <div className="admin-stat-info">
              <span className="admin-stat-value">{loading ? '-' : stats.ligasActivas}</span>
              <span className="admin-stat-label">Ligas Activas</span>
            </div>
          </div>
          
          {!esRecaudador && (
            <div className="admin-stat-card">
              <div className="admin-stat-icon torneos">🎯</div>
              <div className="admin-stat-info">
                <span className="admin-stat-value">{loading ? '-' : stats.torneosActivos}</span>
                <span className="admin-stat-label">Torneos Activos</span>
              </div>
            </div>
          )}
        </section>

        {/* Action Cards */}
        <section className="admin-home-actions">
          <h3 className="admin-section-title">Acciones Rápidas</h3>
          
          <div className="admin-action-grid">
            {/* Ver Ligas */}
            <button className="admin-action-card ligas" onClick={onGoToLigas}>
              <div className="admin-action-icon-wrapper">
                <div className="admin-action-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="8" r="7"></circle>
                    <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
                  </svg>
                </div>
              </div>
              <div className="admin-action-content">
                <h4>Mis Ligas</h4>
                <p>Ver y gestionar ligas</p>
                {!loading && stats.totalLigas > 0 && (
                  <span className="admin-action-count">{stats.totalLigas} liga{stats.totalLigas !== 1 ? 's' : ''}</span>
                )}
              </div>
              <div className="admin-action-arrow">→</div>
            </button>

            {/* Control de partidas: solo recaudador */}
            {onGoToControlPartidas && (
              <button className="admin-action-card ligas" onClick={onGoToControlPartidas}>
                <div className="admin-action-icon-wrapper">
                  <div className="admin-action-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <polyline points="9 14 11 16 15 12"></polyline>
                    </svg>
                  </div>
                </div>
                <div className="admin-action-content">
                  <h4>Control de partidas</h4>
                  <p>Partidas jugadas por bar y liga</p>
                  <span className="admin-action-tag">Recaudación</span>
                </div>
                <div className="admin-action-arrow">→</div>
              </button>
            )}

            {!esRecaudador && (<>
            {/* Ver Torneos */}
            <button className="admin-action-card torneos" onClick={onGoToTorneos}>
              <div className="admin-action-icon-wrapper">
                <div className="admin-action-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
                    <path d="M4 22h16"></path>
                    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
                    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
                    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
                  </svg>
                </div>
              </div>
              <div className="admin-action-content">
                <h4>Mis Torneos</h4>
                <p>Ver y gestionar torneos</p>
                {!loading && stats.totalTorneos > 0 && (
                  <span className="admin-action-count">{stats.totalTorneos} torneo{stats.totalTorneos !== 1 ? 's' : ''}</span>
                )}
              </div>
              <div className="admin-action-arrow">→</div>
            </button>

            {/* Crear Liga */}
            <button className="admin-action-card crear-liga" onClick={onCrearLiga}>
              <div className="admin-action-icon-wrapper">
                <div className="admin-action-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="16"></line>
                    <line x1="8" y1="12" x2="16" y2="12"></line>
                  </svg>
                </div>
              </div>
              <div className="admin-action-content">
                <h4>Crear Liga</h4>
                <p>Nueva competición liguera</p>
                <span className="admin-action-tag">Ida y vuelta / Solo ida</span>
              </div>
              <div className="admin-action-arrow">→</div>
            </button>

            {/* Crear Torneo */}
            <button className="admin-action-card crear-torneo" onClick={onCrearTorneo}>
              <div className="admin-action-icon-wrapper">
                <div className="admin-action-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7"></rect>
                    <rect x="14" y="3" width="7" height="7"></rect>
                    <rect x="14" y="14" width="7" height="7"></rect>
                    <rect x="3" y="14" width="7" height="7"></rect>
                    <line x1="10" y1="6.5" x2="14" y2="6.5"></line>
                    <line x1="10" y1="17.5" x2="14" y2="17.5"></line>
                    <line x1="6.5" y1="10" x2="6.5" y2="14"></line>
                    <line x1="17.5" y1="10" x2="17.5" y2="14"></line>
                  </svg>
                </div>
              </div>
              <div className="admin-action-content">
                <h4>Crear Torneo</h4>
                <p>Nuevo torneo bracket</p>
                <span className="admin-action-tag">Simple / Doble eliminación</span>
              </div>
              <div className="admin-action-arrow">→</div>
            </button>

            {/* Gestión Usuarios */}
            <button className="admin-action-card gestion-usuarios" onClick={onGoToGestionUsuarios}>
              <div className="admin-action-icon-wrapper">
                <div className="admin-action-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
              </div>
              <div className="admin-action-content">
                <h4>Gestión Usuarios</h4>
                <p>Buscar y cambiar contraseñas</p>
                <span className="admin-action-tag">🔐 Admin</span>
              </div>
              <div className="admin-action-arrow">→</div>
            </button>

            </>)}

            {/* Entrenamiento de Cierres */}
            <button className="admin-action-card entrenamiento" onClick={onGoToTraining}>
              <div className="admin-action-icon-wrapper">
                <div className="admin-action-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <circle cx="12" cy="12" r="6"></circle>
                    <circle cx="12" cy="12" r="2"></circle>
                  </svg>
                </div>
              </div>
              <div className="admin-action-content">
                <h4>Entrenamiento</h4>
                <p>Practica y juega partidas</p>
                <span className="admin-action-tag">🎯 Cierres, X01 y más</span>
              </div>
              <div className="admin-action-arrow">→</div>
            </button>
          </div>
        </section>

        {/* Quick Tips */}
        <section className="admin-home-tips">
          <div className="admin-tip">
            <span className="admin-tip-icon">💡</span>
            <p><strong>Consejo:</strong> Las ligas de solo ida son ideales para grupos grandes. Activa la opción de apuntadores para liguillas pequeñas.</p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdminHome;
